import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const STATUS_COLORS = {
  pending: 'badge-amber',
  completed: 'badge-green',
  missed: 'badge-red',
  manual_entry: 'badge-blue',
  manual_pending: 'badge-amber',
}

const STATUS_ICONS = {
  pending: '⏳',
  completed: '✅',
  missed: '❌',
  manual_entry: '📝',
  manual_pending: '📋',
}

export default function Tasks() {
  const { user, profile, isAdmin, isAdminOrManager } = useAuth()
  const [tab, setTab] = useState('today')
  const [tasks, setTasks] = useState([])
  const [myLogs, setMyLogs] = useState([])
  const [teamLogs, setTeamLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)
  const [selectedDate, setSelectedDate] = useState(ugandaDate())

  useEffect(() => { loadData() }, [tab, selectedDate])

  const loadData = async () => {
    setLoading(true)
    const [taskRes, myLogRes, teamLogRes] = await Promise.all([
      supabase.from('task_definitions').select('*, task_assignments(*, profiles(full_name, email))').eq('is_active', true).order('due_time'),
      supabase.from('task_logs').select('*, task_definitions(name, section, due_time)').eq('staff_id', user.id).eq('log_date', selectedDate),
      isAdminOrManager
        ? supabase.from('task_logs').select('*, task_definitions(name, due_time), profiles!staff_id(full_name)').eq('log_date', selectedDate).order('created_at', { ascending: false })
        : Promise.resolve({ data: [] })
    ])
    setTasks(taskRes.data || [])
    setMyLogs(myLogRes.data || [])
    setTeamLogs(teamLogRes.data || [])
    setLoading(false)
  }

  const handleMarkManual = async (taskId) => {
    const now = new Date()
    const hour = now.getHours()
    // Morning (before noon) → deadline midday, Afternoon → EOD
    const deadline = new Date()
    if (hour < 12) {
      deadline.setHours(12, 0, 0, 0)
    } else {
      deadline.setHours(17, 0, 0, 0)
    }
    const { error } = await supabase.from('task_logs').upsert({
      task_id: taskId, staff_id: user.id, log_date: selectedDate,
      status: 'manual_pending', is_manual: true,
      manual_marked_by: user.id, manual_marked_at: now.toISOString(),
      manual_deadline: deadline.toISOString(),
      notes: 'Form being filled manually. Will be logged online before deadline.'
    }, { onConflict: 'task_id,staff_id,log_date' })
    if (!error) { setMsg({ type:'success', text:`Marked as manual entry. Deadline: ${deadline.toLocaleTimeString('en-UG', { hour:'2-digit', minute:'2-digit' })}` }); loadData() }
    setTimeout(() => setMsg(null), 4000)
  }

  const handleMarkComplete = async (taskId, notes = '') => {
    const { error } = await supabase.from('task_logs').upsert({
      task_id: taskId, staff_id: user.id, log_date: selectedDate,
      status: 'completed', completed_at: new Date().toISOString(), notes
    }, { onConflict: 'task_id,staff_id,log_date' })
    if (!error) { setMsg({ type:'success', text:'Task marked complete.' }); loadData() }
    setTimeout(() => setMsg(null), 3000)
  }

  const getMyLog = (taskId) => myLogs.find(l => l.task_id === taskId)

  const myAssignedTasks = tasks.filter(t =>
    t.task_assignments?.some(a => a.staff_id === user.id && a.is_active)
  )

  const now = new Date()
  const overdueTasks = myAssignedTasks.filter(t => {
    const log = getMyLog(t.id)
    if (log?.status === 'completed') return false
    const [h, m] = (t.due_time || '16:30').split(':').map(Number)
    const dueTime = new Date(); dueTime.setHours(h, m, 0, 0)
    return now > dueTime && selectedDate === ugandaDate()
  })

  const completedToday = myLogs.filter(l => l.status === 'completed').length

  return (
    <AppLayout title="My Tasks">
      <div className="section-header">
        <div className="section-icon">✅</div>
        <div className="section-info">
          <h2>Tasks</h2>
          <p>Your daily responsibilities. Complete each task on time and log it in the system.</p>
        </div>
        <input type="date" className="form-input" style={{ width:160, marginLeft:'auto' }} value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {/* Summary */}
      <div className="kpi-grid" style={{ marginBottom:18 }}>
        <div className="kpi green"><div className="kpi-label">My Tasks Today</div><div className="kpi-value">{myAssignedTasks.length}</div></div>
        <div className="kpi green"><div className="kpi-label">Completed</div><div className="kpi-value">{completedToday}</div></div>
        <div className={`kpi ${overdueTasks.length>0?'red':''}`}><div className="kpi-label">Overdue</div><div className="kpi-value">{overdueTasks.length}</div></div>
        {isAdminOrManager && <div className="kpi"><div className="kpi-label">Team Completed</div><div className="kpi-value">{teamLogs.filter(l=>l.status==='completed').length}/{teamLogs.length}</div></div>}
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='today'?'active':''}`} onClick={() => setTab('today')}>📋 My Tasks</button>
        {isAdminOrManager && <button className={`tab ${tab==='team'?'active':''}`} onClick={() => setTab('team')}>👥 Team Tasks</button>}
      </div>

      {tab === 'today' && (
        <div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : myAssignedTasks.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">✅</div><h3>No tasks assigned to you</h3><p>Contact your admin to assign tasks to your account.</p></div>
          ) : myAssignedTasks.map(task => {
            const log = getMyLog(task.id)
            const [h, m] = (task.due_time||'16:30').split(':').map(Number)
            const dueTime = new Date(); dueTime.setHours(h,m,0,0)
            const isOverdue = now > dueTime && selectedDate === ugandaDate()
            const isCompleted = log?.status === 'completed'
            const isManual = log?.status === 'manual_pending'

            return (
              <div key={task.id} className="card" style={{
                marginBottom:12,
                borderLeft:`4px solid ${isCompleted?'var(--green-bright)':isOverdue?'var(--red)':isManual?'var(--blue)':'var(--amber)'}`,
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                      <span style={{ fontSize:'1.2rem' }}>{STATUS_ICONS[log?.status||'pending']}</span>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', fontWeight:700, color:'var(--green-deep)' }}>{task.name}</div>
                      <span className={`badge ${STATUS_COLORS[log?.status||'pending']}`}>{log?.status||'pending'}</span>
                    </div>
                    {task.description && <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:6 }}>{task.description}</div>}
                    <div style={{ display:'flex', gap:12, fontSize:'0.72rem', fontFamily:"'DM Mono',monospace" }}>
                      <span style={{ color:isOverdue&&!isCompleted?'var(--red)':'var(--amber)' }}>
                        🕐 Due by {task.due_time?.slice(0,5) || '16:30'}
                        {isOverdue && !isCompleted && ' — OVERDUE'}
                      </span>
                      {task.section && <span style={{ color:'var(--text-muted)' }}>📂 {task.section}</span>}
                      {task.frequency !== 'daily' && <span className="badge badge-grey">{task.frequency}</span>}
                    </div>
                    {isManual && log?.manual_deadline && (
                      <div style={{ marginTop:6, fontSize:'0.75rem', color:'var(--blue)', fontWeight:600 }}>
                        📝 Manual entry — log online before {new Date(log.manual_deadline).toLocaleTimeString('en-UG', { hour:'2-digit', minute:'2-digit' })}
                      </div>
                    )}
                    {log?.notes && <div style={{ marginTop:4, fontSize:'0.75rem', color:'var(--text-muted)', fontStyle:'italic' }}>{log.notes}</div>}
                  </div>

                  {!isCompleted && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => handleMarkComplete(task.id)}>
                        ✅ Mark Done
                      </button>
                      {task.allows_manual && !isManual && (
                        <button className="btn btn-ghost btn-sm" onClick={() => handleMarkManual(task.id)}>
                          📝 Manual Entry
                        </button>
                      )}
                      {task.section && (
                        <a href={`/${task.section}`} className="btn btn-secondary btn-sm" style={{ textDecoration:'none', textAlign:'center' }}>
                          → Open Form
                        </a>
                      )}
                    </div>
                  )}
                  {isCompleted && (
                    <div style={{ fontSize:'0.72rem', color:'var(--green-mid)', fontFamily:"'DM Mono',monospace" }}>
                      ✅ Done {log.completed_at ? new Date(log.completed_at).toLocaleTimeString('en-UG', { hour:'2-digit', minute:'2-digit' }) : ''}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {tab === 'team' && isAdminOrManager && (
        <div className="card">
          <div className="card-title">Team Task Status — {new Date(selectedDate).toLocaleDateString('en-UG', { weekday:'long', day:'numeric', month:'long' })}</div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : teamLogs.length === 0 ? <div className="empty-state"><div className="empty-icon">👥</div><h3>No task logs for this date</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Staff Member</th><th>Task</th><th>Due</th><th>Status</th><th>Completed At</th><th>Notes</th></tr></thead>
                <tbody>
                  {teamLogs.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontWeight:600 }}>{log.profiles?.full_name||'—'}</td>
                      <td>{log.task_definitions?.name||'—'}</td>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.78rem' }}>{log.task_definitions?.due_time?.slice(0,5)||'—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[log.status]}`}>{STATUS_ICONS[log.status]} {log.status}</span></td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{log.completed_at?new Date(log.completed_at).toLocaleTimeString('en-UG',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{log.notes||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
